import { useEffect, useState } from "react";
import { api, INR2, INR } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown } from "lucide-react";
import DepositModal from "@/components/modals/DepositModal";
import MetalToggle from "@/components/MetalToggle";
import EtfList from "@/components/EtfList";
import { cn } from "@/lib/utils";

export default function Market() {
  const [mode, setMode] = useState("spot"); // spot | etf
  const [history, setHistory] = useState([]);
  const [prices, setPrices] = useState(null);
  const [range, setRange] = useState("30");
  const [metal, setMetal] = useState("gold");
  const [openDeposit, setOpenDeposit] = useState(false);

  const load = async (r = range, m = metal) => {
    const [h, p] = await Promise.all([
      api.get(`/market/history?days=${r}&metal=${m}`),
      api.get("/market/price"),
    ]);
    setHistory(h.data.history || []);
    setPrices(p.data);
  };

  useEffect(() => { if (mode === "spot") load(range, metal); }, [range, metal, mode]);

  const isGold = metal === "gold";
  const rate = isGold ? prices?.price_24k_inr_per_gram : prices?.price_silver_inr_per_gram;
  const alt = isGold ? prices?.price_22k_inr_per_gram : null;
  const changePct = isGold ? prices?.change_pct_24h : prices?.change_pct_24h_silver;
  const up = (changePct || 0) >= 0;
  const dataKey = isGold ? "price_24k" : "price_silver";
  const stroke = isGold ? "#E8B84B" : "#D4D4D8";
  const activeDot = isGold ? "#C9922A" : "#8B8B8B";

  return (
    <div className="pt-6 pb-4">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Market</div>
      <h1 className="mt-1 font-display text-4xl font-semibold text-brand-cream">
        {mode === "spot" ? (isGold ? "Today\u2019s gold rate" : "Today\u2019s silver rate") : "Gold ETFs"}
      </h1>

      {/* Mode switch — Spot vs ETF */}
      <div className="mt-4 inline-flex items-center rounded-full bg-brand-sand2 border border-brand-line-faint p-1">
        {[
          { v: "spot", label: "Spot (physical)" },
          { v: "etf",  label: "Gold ETFs" },
        ].map((o) => (
          <button
            key={o.v}
            data-testid={`market-mode-${o.v}`}
            onClick={() => setMode(o.v)}
            className={cn(
              "h-9 px-4 rounded-full text-xs font-semibold transition-transform active:scale-95",
              mode === o.v
                ? "bg-gold-gradient text-brand-page shadow-gold"
                : "text-brand-slate hover:text-brand-cream"
            )}
          >
            {o.label}
          </button>
        ))}
      </div>

      {mode === "spot" ? (
        <>
          <div className="mt-4 flex justify-start">
            <MetalToggle value={metal} onChange={setMetal} testId="market-metal-toggle" />
          </div>

          <div data-testid="market-price-card" className="mt-5 rounded-3xl border border-brand-line bg-brand-sand p-5 shadow-soft">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand-slate">
                  {isGold ? "24k · per gram" : "Silver · per gram"}
                </div>
                <div className="font-display text-4xl text-brand-cream tabular-nums mt-1">
                  {rate ? INR2(rate) : "—"}
                </div>
                {alt && (
                  <div className="text-sm text-brand-slate tabular-nums">22k · {INR2(alt)}/g</div>
                )}
                {!alt && (
                  <div className="text-sm text-brand-slate tabular-nums">10g · {rate ? INR2(rate * 10) : "—"}</div>
                )}
              </div>
              <div className={`rounded-full px-3 py-1.5 flex items-center gap-1 text-sm font-semibold ${up ? "bg-emerald-900/30 text-brand-secondary" : "bg-red-900/30 text-brand-danger"}`}>
                {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {changePct != null ? changePct : 0}%
              </div>
            </div>

            <Tabs value={range} onValueChange={setRange} className="mt-5">
              <TabsList data-testid="market-range-tabs" className="rounded-full bg-brand-sand2 border border-brand-line-faint p-1">
                <TabsTrigger value="7" data-testid="range-7d" className="rounded-full data-[state=active]:bg-brand-primary data-[state=active]:text-brand-page">7D</TabsTrigger>
                <TabsTrigger value="30" data-testid="range-30d" className="rounded-full data-[state=active]:bg-brand-primary data-[state=active]:text-brand-page">30D</TabsTrigger>
                <TabsTrigger value="90" data-testid="range-90d" className="rounded-full data-[state=active]:bg-brand-primary data-[state=active]:text-brand-page">90D</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="mt-4 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(201,146,42,0.15)" />
                  <XAxis dataKey="date" hide />
                  <YAxis
                    domain={["dataMin - 30", "dataMax + 30"]}
                    tick={{ fontSize: 10, fill: "rgba(240,230,200,0.55)" }}
                    tickFormatter={(v) => `\u20B9${Math.round(v)}`}
                    width={60}
                  />
                  <Tooltip
                    contentStyle={{ background: "#120E06", border: "1px solid rgba(201,146,42,0.25)", borderRadius: 12, fontSize: 12, color: "#F0E6C8" }}
                    formatter={(v) => [INR2(v), isGold ? "24k /g" : "Silver /g"]}
                  />
                  <Line
                    type="monotone"
                    dataKey={dataKey}
                    stroke={stroke}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4, fill: activeDot }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <Button
            data-testid="market-buy-btn"
            onClick={() => setOpenDeposit(true)}
            className="mt-5 w-full h-14 rounded-full bg-gold-gradient text-brand-page font-semibold text-base shadow-gold"
          >
            Buy {isGold ? "Gold" : "Silver"} at {rate ? INR(rate) : "—"}/g
          </Button>

          <DepositModal open={openDeposit} onOpenChange={setOpenDeposit} onDone={() => load(range, metal)} defaultMetal={metal} />
        </>
      ) : (
        <div className="mt-5">
          <div className="rounded-2xl bg-brand-sand2 border border-brand-line-faint p-3.5 mb-4 flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-brand-secondary/20 flex items-center justify-center shrink-0">
              <span className="text-brand-secondary font-bold text-sm">✓</span>
            </div>
            <div className="text-[11px] leading-relaxed text-brand-slate">
              <span className="font-semibold text-brand-cream">SEBI-regulated · Govt-backed</span>{" "}
              gold ETFs invest in 99.5%+ pure physical bullion. Units settle T+1 to your demat.
            </div>
          </div>
          <EtfList />
        </div>
      )}
    </div>
  );
}
